import config from 'src/config';
import engineApiSlice from 'src/shared/state/api-slices/engineApiSlice';
import { executeEngineQuery } from 'src/app/portfolio/state/api/engineQueryUtils';
import { siteNatureRiskOverviewQuery, type SiteNatureRiskOverviewResult } from './siteNatureRiskOverviewQuery';
import { siteStateOfNatureQuery, type SiteStateOfNatureResult } from './siteStateOfNatureQuery';
import { siteThreatenedSpeciesQuery, type SiteThreatenedSpeciesResult } from './siteThreatenedSpeciesQuery';

export type SiteNatureRiskOverviewQueryArgs = {
    organisationBusinessCentralId: string;
    siteId: string;
};

export type SiteThreatenedSpeciesQueryArgs = SiteNatureRiskOverviewQueryArgs & {
    page: number;
    pageSize: number;
};

type SetSiteNatureStatusArgs = {
    organisationBusinessCentralId: string;
    siteId: string;
    content: string;
};

type SetNatureStatusResponse = {
    content: string;
    createdAt: string;
};

const siteNatureRiskEngineSlice = engineApiSlice.injectEndpoints({
    endpoints: builder => ({
        getSiteNatureRiskOverview: builder.query<SiteNatureRiskOverviewResult, SiteNatureRiskOverviewQueryArgs>({
            queryFn: arg =>
                executeEngineQuery<SiteNatureRiskOverviewResult>(
                    `${config.engineApiUrl.replace(/\/$/, '')}/query`,
                    siteNatureRiskOverviewQuery,
                    {
                        organisationBusinessCentralId: arg.organisationBusinessCentralId,
                        siteId: arg.siteId,
                    }
                ),
            providesTags: (_result, _error, arg) => [{ type: 'site' as const, id: arg.siteId }],
        }),
        getSiteStateOfNature: builder.query<SiteStateOfNatureResult, SiteNatureRiskOverviewQueryArgs>({
            queryFn: arg =>
                executeEngineQuery<SiteStateOfNatureResult>(`${config.engineApiUrl.replace(/\/$/, '')}/query`, siteStateOfNatureQuery, {
                    organisationBusinessCentralId: arg.organisationBusinessCentralId,
                    siteId: arg.siteId,
                }),
            providesTags: (_result, _error, arg) => [{ type: 'site' as const, id: arg.siteId }],
        }),
        getSiteThreatenedSpecies: builder.query<SiteThreatenedSpeciesResult, SiteThreatenedSpeciesQueryArgs>({
            queryFn: arg =>
                executeEngineQuery<SiteThreatenedSpeciesResult>(
                    `${config.engineApiUrl.replace(/\/$/, '')}/query`,
                    siteThreatenedSpeciesQuery,
                    {
                        organisationBusinessCentralId: arg.organisationBusinessCentralId,
                        siteId: arg.siteId,
                        page: arg.page,
                        pageSize: arg.pageSize,
                    }
                ),
            providesTags: (_result, _error, arg) => [{ type: 'site' as const, id: arg.siteId }],
        }),
        setSiteNatureStatus: builder.mutation<SetNatureStatusResponse, SetSiteNatureStatusArgs>({
            query: params => ({
                url: `/orgs/${params.organisationBusinessCentralId}/sites/${params.siteId}/nature-status`,
                method: 'POST',
                body: { content: params.content },
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    siteNatureRiskEngineSlice.util.updateQueryData(
                        'getSiteNatureRiskOverview',
                        { organisationBusinessCentralId: arg.organisationBusinessCentralId, siteId: arg.siteId },
                        draft => {
                            if (draft.site?.natureStatus) {
                                draft.site.natureStatus.content = arg.content;
                            } else if (draft.site) {
                                draft.site.natureStatus = { content: arg.content, createdAt: '' };
                            }
                        }
                    )
                );
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        siteNatureRiskEngineSlice.util.updateQueryData(
                            'getSiteNatureRiskOverview',
                            { organisationBusinessCentralId: arg.organisationBusinessCentralId, siteId: arg.siteId },
                            draft => {
                                if (draft.site) {
                                    draft.site.natureStatus = { content: data.content, createdAt: data.createdAt };
                                }
                            }
                        )
                    );
                } catch {
                    patchResult.undo();
                }
            },
            invalidatesTags: (_result, _error, arg) => [{ type: 'site' as const, id: arg.siteId }],
        }),
    }),
});

export const {
    useGetSiteNatureRiskOverviewQuery,
    useGetSiteStateOfNatureQuery,
    useGetSiteThreatenedSpeciesQuery,
    useSetSiteNatureStatusMutation,
} = siteNatureRiskEngineSlice;

export default siteNatureRiskEngineSlice;
